<template>
  <AppCard accent="bg-green-600" no-padding>
    <div class="px-7 pt-6 pb-5">
      <!-- Top row: company name + actions -->
      <div class="flex items-start justify-between gap-6">
        <div>
          <h1
            class="text-navy-900 text-[22px] leading-tight font-bold tracking-tight"
          >
            {{ company.name }}
          </h1>
          <p class="text-navy-400 mt-1 text-[13px]">Company Settings</p>
        </div>
        <AppButton
          v-if="isAdmin"
          variant="secondary"
          size="sm"
          @click="$emit('edit')"
        >
          Edit
        </AppButton>
      </div>

      <!-- Stats row -->
      <div class="border-navy-100 mt-4 flex items-center gap-0 border-t pt-3.5">
        <!-- Members -->
        <div class="flex flex-1 items-center gap-2.5">
          <div
            class="bg-navy-50 flex h-9 w-9 items-center justify-center rounded-lg"
          >
            <i class="pi pi-users text-navy-400 text-[15px]" />
          </div>
          <div>
            <div class="text-navy-800 text-lg leading-none font-bold">
              {{ company.members.length }}
            </div>
            <div class="text-[11px] text-neutral-400">Members</div>
          </div>
        </div>

        <div class="mx-4 h-8 w-px bg-neutral-200" />

        <!-- Agents -->
        <div class="flex flex-1 items-center gap-2.5">
          <div
            class="bg-navy-50 flex h-9 w-9 items-center justify-center rounded-lg"
          >
            <i class="pi pi-server text-navy-400 text-[15px]" />
          </div>
          <div>
            <div class="text-navy-800 text-lg leading-none font-bold">
              {{ company.agentRegistrations.length }}
            </div>
            <div class="flex items-center gap-1 text-[11px] text-neutral-400">
              Agents
              <template v-if="onlineCount > 0">
                <span class="text-neutral-300">&middot;</span>
                <span class="inline-flex items-center gap-1">
                  <span
                    class="inline-block h-1.5 w-1.5 rounded-full bg-green-600"
                  />
                  {{ onlineCount }} online
                </span>
              </template>
              <template v-if="offlineCount > 0">
                <span class="text-neutral-300">&middot;</span>
                <span class="inline-flex items-center gap-1">
                  <span
                    class="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
                  />
                  {{ offlineCount }} offline
                </span>
              </template>
            </div>
          </div>
        </div>

        <div class="mx-4 h-8 w-px bg-neutral-200" />

        <!-- Orders -->
        <div class="flex flex-1 items-center gap-2.5">
          <div
            class="bg-navy-50 flex h-9 w-9 items-center justify-center rounded-lg"
          >
            <i class="pi pi-file text-navy-400 text-[15px]" />
          </div>
          <div>
            <div class="text-navy-800 text-lg leading-none font-bold">
              {{ company.ordersCount }}
            </div>
            <div class="text-[11px] text-neutral-400">
              Orders &middot; {{ company.activeOrdersCount }} active
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppCard>
</template>

<script setup lang="ts">
import { computed } from "vue"

import type { CompanyType } from "../../pages/Company/types"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"

interface Props {
  company: CompanyType
  isAdmin: boolean
}

const props = defineProps<Props>()

defineEmits<{
  edit: []
}>()

const onlineCount = computed(
  () =>
    props.company.agentRegistrations.filter((a) => a.status === "connected")
      .length,
)

const offlineCount = computed(
  () =>
    props.company.agentRegistrations.filter((a) => a.status !== "connected")
      .length,
)
</script>
